import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import pluralize from 'pluralize';

import UploadProgressTrackingFile from './UploadProgressTrackingFile';
import Label from '../../../../components/global/Label';
import Button from '../../../../components/global/Button';
import useAchievementUnlocked from '../../hooks/useAchievementUnlocked';
import { ACHIEVEMENT_ACTIONS } from '../../../../constants/achievements';
import { uploadCancel as uploadCancelAction } from '../../redux/fileUpload/fileUploadActions';
import { requestConfirm as requestConfirmAction } from '../../../../actions/modal-actions';

const propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    }),
  ),
  inProgress: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({
        name: PropTypes.string,
      }),
      progressEvent: PropTypes.object,
      startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
  ),
  completed: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({
        name: PropTypes.string,
      }),
      photoId: PropTypes.string,
    }),
  ),
  failed: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({
        name: PropTypes.string,
      }),
      error: PropTypes.object,
    }),
  ),
  uploading: PropTypes.bool,
  complete: PropTypes.bool,
  className: PropTypes.string,
  forceOpen: PropTypes.bool,
  onDoneBtnClick: PropTypes.func,
  doneBtnText: PropTypes.string,
  doneBtnProps: PropTypes.object,
  onRetryBtnClick: PropTypes.func,
  doneBtnAlwaysVisible: PropTypes.bool,
  fileLabel: PropTypes.string,
  cancelEnabled: PropTypes.bool,
  canceled: PropTypes.bool,
  uploadCancel: PropTypes.func,
  requestConfirm: PropTypes.func,
};

const UploadProgressTracking = ({
  files = [],
  inProgress = [],
  completed = [],
  failed = [],
  uploading,
  complete,
  className,
  forceOpen = false,
  onDoneBtnClick,
  doneBtnText = 'Done',
  onRetryBtnClick,
  doneBtnAlwaysVisible = false,
  doneBtnProps = {},
  fileLabel = 'photo',
  canceled,
  uploadCancel,
  requestConfirm,
  cancelEnabled = false,
}) => {
  const { checkAndReportAchievementUnlocked } = useAchievementUnlocked();

  if (!uploading && !forceOpen) {
    return null;
  }

  const totalFiles = files.length;
  const processedFiles = Math.min(
    failed.length + completed.length,
    files.length,
  );

  const classes = classNames({
    'upload-progress-tracker': true,
    [className]: className,
  });

  const inProgressNames = inProgress.map(uploadItem => uploadItem.file.name);
  const completedFilesNames = completed.map(uploadItem => uploadItem.file.name);
  const failedFilesNames = failed.map(uploadItem => uploadItem.file.name);
  const notStarted = files
    .filter(
      file =>
        !inProgressNames.includes(file.name) &&
        !failedFilesNames.includes(file.name) &&
        !completedFilesNames.includes(file.name),
    )
    // Convert these into pseudo photoItems:
    .map(file => ({ file }));

  const incompleteFiles = [
    ...inProgress,
    ...failed,
    // Show those "waiting" last.
    ...notStarted,
  ];

  useEffect(() => {
    if (completedFilesNames.length > 0) {
      checkAndReportAchievementUnlocked([
        ACHIEVEMENT_ACTIONS.SHUTTERBUG_PHOTO_UPLOAD,
      ]);
    }
  }, [completedFilesNames.length > 0]);

  if (!uploading && files.length === 0 && !canceled) {
    return (
      <div className={classes}>
        <Label id="upload-progress-tracker-title" labelType="expanded">
          Upload progress
        </Label>
        <p className="text-faded">
          Waiting for {pluralize(fileLabel, 0)} to be selected&hellip;
        </p>
      </div>
    );
  }

  const onCancel = () => {
    requestConfirm({
      message:
        'Are you certain you want to cancel the current upload? Already uploaded photos will not be affected, but remaining ones will not be processed.',
      confirmText: 'Yes, cancel',
      onConfirm: async () => {
        uploadCancel();
      },
      onCancel: () => {},
      confirmBtnStyle: 'danger',
    });
  };

  return (
    <div className={classes}>
      <div className="upload-progress-tracker-header">
        <div className="upload-progress-tracker-title">
          <div className="d-flex justify-content-between align-items-center">
            <Label id="upload-progress-title" labelType="expanded">
              Upload progress
            </Label>

            {!complete &&
              incompleteFiles.length > 0 &&
              !canceled &&
              cancelEnabled && (
                <Button
                  className="mr-1 text-danger text-extra-bold"
                  btnSize="sm"
                  onClick={onCancel}
                  btnStyle="link"
                >
                  Cancel
                </Button>
              )}

            {(complete || doneBtnAlwaysVisible) && onDoneBtnClick && (
              <Button
                {...doneBtnProps}
                className="upload-progress-tracker-done text-extra-bold"
                btnSize="sm"
                onClick={onDoneBtnClick}
                btnStyle="link"
              >
                {doneBtnText}
              </Button>
            )}
          </div>
          {canceled && <p className="text-danger">Upload has been canceled</p>}
          {!canceled && (
            <Fragment>
              {complete ? (
                <p>Uploading has completed</p>
              ) : (
                <p>
                  Uploaded {processedFiles} of {totalFiles}
                  &hellip;
                </p>
              )}
            </Fragment>
          )}
        </div>
      </div>
      {failed.length > 0 && (
        <Fragment>
          <p className="text-danger text-sm mt-0">
            Looks like {failed.length} {pluralize(fileLabel, failed.length)}{' '}
            failed to upload.{' '}
            {onRetryBtnClick && (
              <Button
                btnStyle="link"
                btnSize="sm"
                onClick={() =>
                  onRetryBtnClick(failed.map(uploadItem => uploadItem.file))
                }
              >
                Retry failed {pluralize(fileLabel, failed.length)}
              </Button>
            )}
          </p>
        </Fragment>
      )}
      <div className="upload-progress-tracker-sections">
        <div className="upload-progress-tracker-section">
          <Label
            id="upload-progress-tracker-incomplete"
            labelType="expanded"
            className="upload-progress-tracker-subtitle"
          >
            Uploading
          </Label>
          {incompleteFiles.length > 0 ? (
            incompleteFiles.map(uploadItem => (
              <UploadProgressTrackingFile
                key={uploadItem.file.name}
                uploadItem={uploadItem}
              />
            ))
          ) : (
            <p>
              There are no more {pluralize(fileLabel, 0)} to process&hellip;
            </p>
          )}
        </div>

        <div className="upload-progress-tracker-section">
          <Label
            id="upload-progress-tracker-complete"
            labelType="expanded"
            className="upload-progress-tracker-subtitle"
          >
            Completed{' '}
            {completed.length > 0 && (
              <Fragment>
                ({completed.length} {pluralize(fileLabel, completed.length)})
              </Fragment>
            )}
          </Label>
          {completed.length > 0 ? (
            completed.map(uploadItem => (
              <UploadProgressTrackingFile
                key={uploadItem.file.name}
                uploadItem={uploadItem}
              />
            ))
          ) : (
            <p className="text-faded">Waiting for upload to complete&hellip;</p>
          )}
        </div>
      </div>
    </div>
  );
};

UploadProgressTracking.propTypes = propTypes;

export { UploadProgressTracking as UploadProgressTrackingComponent };

const mapStateToProps = ({ pro }) => ({
  files: pro.fileUpload.files,
  inProgress: pro.fileUpload.inProgress,
  completed: pro.fileUpload.completed,
  failed: pro.fileUpload.failed,
  uploading: pro.fileUpload.uploading,
  complete: pro.fileUpload.complete,
  canceled: pro.fileUpload.canceled,
});

const mapDispatchToProps = {
  uploadCancel: uploadCancelAction,
  requestConfirm: requestConfirmAction,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  UploadProgressTracking,
);
